/**
 * TaskStatusControl — a compact, clickable status chip that lets the viewer
 * change a task's status in place. One control serves two needs:
 *   - a supervisor/admin changing status from the project Tasks list, and
 *   - anyone who can act reverting an accidental tap (e.g. undo "Start").
 *
 * The chip mirrors StatusBadge's look (color is by status — ADR-0010) and adds
 * a chevron to signal it's interactive. Clicking opens a lightweight menu of the
 * statuses the viewer may move to from the current one. The option set mirrors
 * the backend guard (apps/api/src/routes/tasks.ts → assertTransitionAllowed):
 * movement among the ACTIVE statuses is free, `blocked` and `complete` are
 * offered, and `cancelled` / reopening a terminal task are supervisor-only. The
 * backend is the source of truth — anything the UI lets slip through comes back
 * as a clear error toast.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import type { TaskStatus } from '@vari-crew/schemas';
import { useTransitionTask } from '@/api/tasks';
import { cn } from '@/lib/cn';

/**
 * Statuses the menu lets the viewer set directly. Collapsed to the two-button
 * crew flow (demo #7): a task is Scheduled or In progress; en_route / on_site /
 * paused were retired from the UI (a supervisor can still set them via the API,
 * and any legacy value still renders correctly via the label/class maps below).
 */
const ACTIVE_STATUSES: readonly TaskStatus[] = ['scheduled', 'in_progress'];

/** Forward ordering — used to mark a backward pick (a revert) subtly. */
const STATUS_ORDER: TaskStatus[] = [
  'scheduled',
  'en_route',
  'on_site',
  'in_progress',
  'paused',
  'blocked',
  'complete',
  'cancelled',
];

/** Sentence-case labels — task statuses have no shared label map in schemas. */
const STATUS_LABEL: Record<TaskStatus, string> = {
  scheduled: 'Scheduled',
  en_route: 'En route',
  on_site: 'On site',
  in_progress: 'In progress',
  paused: 'Paused',
  blocked: 'Blocked',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

/**
 * Status → chip color classes. Matches the CrewTaskDetail STATUS_BADGE map so
 * the same task reads identically across screens.
 */
const STATUS_CLASSES: Record<TaskStatus, string> = {
  scheduled: 'bg-status-scheduled text-status-scheduled-fg',
  en_route: 'bg-status-enroute text-status-enroute-fg',
  on_site: 'bg-status-onsite text-status-onsite-fg',
  in_progress: 'bg-status-onsite text-status-onsite-fg',
  paused: 'bg-status-pendingcloseout text-status-pendingcloseout-fg',
  blocked: 'bg-status-blocked text-status-blocked-fg',
  complete: 'bg-status-complete text-status-complete-fg',
  cancelled: 'bg-fill text-ink-dim',
};

/**
 * The statuses the viewer can move to from `current`, mirroring the backend
 * guard so the menu only offers what will succeed.
 */
function targetsFor(current: TaskStatus, canManage: boolean): TaskStatus[] {
  // A terminal task only reopens (to an active status), and only a supervisor
  // may do it.
  if (current === 'complete' || current === 'cancelled') {
    return canManage ? ACTIVE_STATUSES.filter((s) => s !== current) : [];
  }

  const targets: TaskStatus[] = [];
  // Free movement among the active statuses (covers forward + revert).
  for (const s of ACTIVE_STATUSES) if (s !== current) targets.push(s);
  // Blocked is always available to anyone who can act.
  if (current !== 'blocked') targets.push('blocked');
  // Complete — crew only from on_site/in_progress/paused; supervisors anytime.
  // The backend rejects a premature crew complete, so only offer it when valid.
  if (canManage || current === 'on_site' || current === 'in_progress' || current === 'paused') {
    targets.push('complete');
  }
  // Cancel — supervisor/admin only.
  if (canManage) targets.push('cancelled');
  return targets;
}

export function TaskStatusControl({
  taskId,
  status,
  canManage,
  size = 'sm',
  className,
}: {
  taskId: string;
  status: TaskStatus;
  /** Supervisor/admin — unlocks cancel + reopening a terminal task. */
  canManage: boolean;
  size?: 'sm' | 'xs';
  className?: string;
}) {
  const transition = useTransitionTask(taskId);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const targets = targetsFor(status, canManage);
  const currentRank = STATUS_ORDER.indexOf(status);

  // Outside-click + Escape close (mirrors RecipientAutocomplete).
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function choose(to: TaskStatus) {
    setOpen(false);
    transition.mutate(
      { to },
      {
        onSuccess: () => toast.success('Status updated'),
        onError: (e) => toast.error(e.message),
      },
    );
  }

  return (
    <div ref={wrapRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={transition.isPending || targets.length === 0}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Status: ${STATUS_LABEL[status]}. Change status`}
        className={cn(
          'inline-flex items-center gap-1 rounded-full font-medium transition-opacity',
          'hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ring-offset-bg',
          'disabled:cursor-default disabled:opacity-60',
          size === 'xs' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-0.5 text-xs',
          STATUS_CLASSES[status],
        )}
      >
        {STATUS_LABEL[status]}
        {targets.length > 0 && (
          <ChevronDown className={cn('opacity-70', size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5')} />
        )}
      </button>

      {open && targets.length > 0 && (
        <div
          role="menu"
          id={menuId}
          aria-label="Change status"
          className="absolute right-0 z-50 mt-1 min-w-44 overflow-hidden rounded-md border border-line bg-bg py-1 shadow-lg"
        >
          {targets.map((to) => {
            // A clearly-backward move (e.g. on_site → en_route) is a revert —
            // mark it subtly with a back arrow.
            const isBackward = STATUS_ORDER.indexOf(to) < currentRank && ACTIVE_STATUSES.includes(to);
            return (
              <button
                key={to}
                type="button"
                role="menuitem"
                onClick={() => choose(to)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-ink hover:bg-fill-soft"
              >
                <span
                  className={cn(
                    'inline-block h-2 w-2 shrink-0 rounded-full',
                    STATUS_CLASSES[to],
                  )}
                  aria-hidden
                />
                <span className="flex-1">{STATUS_LABEL[to]}</span>
                {isBackward && (
                  <RotateCcw className="w-3 h-3 shrink-0 text-ink-faint" aria-label="revert" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
